import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Clock, Check, X, Music, CheckCircle, XCircle, MessageSquare, Sparkles, Loader2, AlertTriangle, Flag, Inbox, ShieldCheck, ShieldX, ShieldAlert, Copy, ExternalLink } from 'lucide-react';
import { PEOPLES_PICK_HIDDEN } from '../../utils/featureFlags';
import { subscribeToRequestsByEventId, updateRequest, matchedLinkFields, SongRequest } from '../../services/requests';
import { needsLinkMatch, playModeOf } from '../../services/playModes';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { formatElapsed } from '../../utils/dateUtils';
import { getEventById } from '../../services/events';
import { isWaitingOnCoApprover, shouldRouteToCoApprover, staleCoApprovalFields } from '../../utils/coApprovalRouting';
import { useEventManageContext } from './EventManagePage';
import { analyzeSong, reanalyzeSongWithLyrics, SongAnalysis } from '../../services/songAnalysis';
import { batchGetVerdicts, setSongVerdict } from '../../services/songReviews';
import { SongSearchResult } from '../../services/itunesApi';
import { isrcForMatchedSong } from '../../utils/songIdentity';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import SongMatchModal from '../../components/dj/SongMatchModal';
import { MatchRun, MatchRunStep, startMatchRun, advanceMatchRun } from '../../utils/matchRun';
import { useCoApprovalDjActive } from '../../hooks/useCoApprovalDjActive';
import { useReviewHold } from '../../hooks/useReviewHold';
import { holderLabel, isHeldByOther } from '../../utils/reviewHold';
import { ReviewHoldBanner } from '../../components/dj/ReviewHoldBanner';
import { ReviewedByBadge } from '../../components/dj/ReviewedByBadge';

function linkFieldsFor(
  platformLink: string,
  platformName: string,
  songLinkUrl: string,
  songUpdate: unknown,
  existing: SongRequest | undefined,
): Record<string, unknown> {
  const song = (songUpdate as SongRequest['song'] | undefined) ?? existing?.song;
  if (!song) return { platformLink, platformName, ...(songLinkUrl ? { songLinkUrl } : {}) };
  return { ...matchedLinkFields({ platformLink, platformName, songLinkUrl }, song) };
}

export default function PendingPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const { event: eventData } = useEventManageContext();
  const playMode = playModeOf(eventData);
  const coApprovalEnabled = eventData?.settings?.coApprovalEnabled || false;
  const isLocked = eventData?.locked ?? false;
  const [pendingRequests, setPendingRequests] = useState<SongRequest[]>([]);
  const [nominations, setNominations] = useState<SongRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBulkRejectConfirm, setShowBulkRejectConfirm] = useState(false);
  const [showBulkApproveConfirm, setShowBulkApproveConfirm] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  const [rejectingRequest, setRejectingRequest] = useState<SongRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  // Detected player platform from desktop app (stored in user doc by desktop)

  // AI Analysis modal state
  const [analysisModal, setAnalysisModal] = useState<{
    isOpen: boolean;
    songTitle: string;
    songArtist: string;
    analysis: SongAnalysis | null;
    loading: boolean;
    error: string | null;
  }>({
    isOpen: false, songTitle: '', songArtist: '', analysis: null, loading: false, error: null,
  });
  const [lyricsPaste, setLyricsPaste] = useState({ text: '', loading: false });

  // Platform Match Modal state (managed by SongMatchModal component)
  const [matchingRequest, setMatchingRequest] = useState<SongRequest | null>(null);
  const [pendingMatchIndex, setPendingMatchIndex] = useState(0);
  const [pendingMatchTotal, setPendingMatchTotal] = useState(0);
  const [matchRun, setMatchRun] = useState<MatchRun | null>(null);
  const [songVerdicts, setSongVerdicts] = useState<Map<string, 'passed' | 'blocked'>>(new Map());
  const [matchingNomination, setMatchingNomination] = useState(false);

  // A co-approval DJ reviews the same pending songs; each side reserves a song while its pop-up is open (ITER-194)
  const coApprovalDjActive = useCoApprovalDjActive(eventId, user?.uid);
  const reviewHold = useReviewHold({
    eventId,
    enabled: coApprovalDjActive,
    onExpired: () => {
      handleCloseMatchModal();
      toast('Your hold ran out — the song is back on the list', { icon: '⏱️' });
    },
  });
  const [holdClockMs, setHoldClockMs] = useState(() => Date.now());
  useEffect(() => {
    if (!coApprovalDjActive) return;
    const interval = setInterval(() => setHoldClockMs(Date.now()), 5000);
    return () => clearInterval(interval);
  }, [coApprovalDjActive]);
  const heldByHelper = (request: SongRequest) =>
    request.id !== reviewHold.heldRequestId && isHeldByOther(request, 'dj', holdClockMs, coApprovalDjActive);
  const actionDimmed = (request: SongRequest) =>
    processingIds.has(request.id) || heldByHelper(request) ? 'opacity-50 cursor-not-allowed' : '';
  const actionTitle = (request: SongRequest, label: string) =>
    heldByHelper(request) ? `${holderLabel(request.reviewHold)} is reviewing this song` : label;

  useEffect(() => {
    if (!eventId) return;

    const unsub = subscribeToRequestsByEventId(eventId, (allRequests) => {
      const pending = allRequests.filter(r => r.status === 'pending' && !r.superRequest);
      const pendingNominations = allRequests.filter(r => r.status === 'pending' && r.superRequest);
      setPendingRequests(pending);
      setNominations(pendingNominations);

      if (pending.length > 0) {
        batchGetVerdicts(pending.map(r => ({ title: r.song.title, artist: r.song.artist }))).then(setSongVerdicts);
      }

      setLoading(false);
    });

    return () => unsub();
  }, [eventId, user?.uid]);

  // --- AI Analysis ---
  const handleAnalyzeSong = async (title: string, artist: string) => {
    setAnalysisModal({ isOpen: true, songTitle: title, songArtist: artist, analysis: null, loading: true, error: null });
    try {
      const result = await analyzeSong(title, artist);
      setAnalysisModal(prev => ({ ...prev, analysis: result, loading: false }));
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : 'Failed to analyze song';
      setAnalysisModal(prev => ({ ...prev, loading: false, error: errMsg }));
      toast.error('Failed to analyze song');
    }
  };

  const handleCloseAnalysisModal = () => {
    setAnalysisModal({ isOpen: false, songTitle: '', songArtist: '', analysis: null, loading: false, error: null });
    setLyricsPaste({ text: '', loading: false });
  };

  const handleReanalyzeWithLyrics = async () => {
    if (!lyricsPaste.text.trim()) return;
    setLyricsPaste(prev => ({ ...prev, loading: true }));
    try {
      const result = await reanalyzeSongWithLyrics(analysisModal.songTitle, analysisModal.songArtist, lyricsPaste.text);
      setAnalysisModal(prev => ({ ...prev, analysis: result }));
      setLyricsPaste({ text: '', loading: false });
      toast.success('Re-analyzed with provided lyrics');
    } catch {
      setLyricsPaste(prev => ({ ...prev, loading: false }));
      toast.error('Failed to re-analyze with lyrics');
    }
  };

  const handleSetVerdict = async (title: string, artist: string, verdict: 'passed' | 'blocked') => {
    if (!user?.uid) return;
    const key = `${title.toLowerCase().trim()}|${artist.toLowerCase().trim()}`;
    const currentVerdict = songVerdicts.get(key);
    const newVerdict = currentVerdict === verdict ? null : verdict;
    try {
      await setSongVerdict(title, artist, newVerdict, user.uid);
      setSongVerdicts(prev => {
        const next = new Map(prev);
        if (newVerdict) next.set(key, newVerdict);
        else next.delete(key);
        return next;
      });
      toast.success(newVerdict ? `Song ${newVerdict}` : 'Verdict cleared');
    } catch {
      toast.error('Failed to update verdict');
    }
  };

  // --- Approve (direct or via match modal) ---
  const approveDirectly = async (requestId: string, spotifyUri?: string) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      // Fresh-read co-approval setting from Firestore to avoid stale context
      let isCoApprovalOn = coApprovalEnabled;
      if (eventId) {
        try {
          const freshEvent = await getEventById(eventId);
          if (freshEvent) isCoApprovalOn = freshEvent.settings.coApprovalEnabled || false;
        } catch { /* fall back to context value */ }
      }
      const routeToCoApprover = shouldRouteToCoApprover(isCoApprovalOn, existing?.coApprovalStatus);
      const targetStatus = existing?.isOverflow ? 'overflow' : 'queued';
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover
          ? { coApprovalStatus: 'pending' }  // Keep status 'pending', send to co-approver
          : { status: targetStatus }          // Normal: move to queued (or overflow for overflow requests)
        ),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        ...(spotifyUri ? { spotifyUri } : {}),
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      await updateRequest(requestId, updateData);
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      const approvalMsg = routeToCoApprover
        ? 'Sent to co-approver for review'
        : existing?.isOverflow
          ? 'Approved to overflow pool'
          : 'Request approved and added to queue';
      toast.success(approvalMsg);
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
  };

  // Approve with a platform-specific link (from Odesli resolution or direct paste)
  const approveWithPlatformLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: SongSearchResult | null
  ) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      let isCoApprovalOn = coApprovalEnabled;
      if (eventId) {
        try {
          const freshEvent = await getEventById(eventId);
          if (freshEvent) isCoApprovalOn = freshEvent.settings.coApprovalEnabled || false;
        } catch { /* fall back */ }
      }
      const routeToCoApprover = shouldRouteToCoApprover(isCoApprovalOn, existing?.coApprovalStatus);
      const targetStatus = existing?.isOverflow ? 'overflow' : 'queued';
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { status: targetStatus }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        preppedInSpotify: platformName === 'Spotify',
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      if (itunesResult) {
        updateData.song = {
          ...existing?.song,
          title: itunesResult.trackName,
          artist: itunesResult.artistName,
          albumArtUrl: itunesResult.artworkUrl100 || existing?.song?.albumArtUrl || '',
          durationMs: itunesResult.trackTimeMillis || existing?.song?.durationMs || 0,
          explicitFlag: itunesResult.trackExplicitness === 'explicit' || existing?.song?.explicitFlag || false,
          itunesTrackId: String(itunesResult.trackId),
          isrc: isrcForMatchedSong(itunesResult, existing?.song ?? {}),
          previewUrl: itunesResult.previewUrl || existing?.song?.previewUrl || null,
        };
        for (const key of Object.keys(updateData.song as Record<string, unknown>)) {
          if ((updateData.song as Record<string, unknown>)[key] === undefined) {
            (updateData.song as Record<string, unknown>)[key] = null;
          }
        }
      }
      Object.assign(updateData, linkFieldsFor(platformLink, platformName, songLinkUrl, updateData.song, existing));
      await updateRequest(requestId, updateData);
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success(routeToCoApprover ? 'Sent to co-approver for review' : `Matched on ${platformName} — added to queue`);
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
  };

  const openMatchModal = async (request: SongRequest, index = 0, total = 0): Promise<boolean> => {
    const outcome = await reviewHold.claim(request.id);
    if (outcome.status === 'held') {
      toast(`${holderLabel(outcome.heldBy)} is reviewing this one`, { icon: '👀' });
      return false;
    }
    if (outcome.status === 'error') toast.error("Couldn't reserve this song — the helper may open it too");
    setMatchingRequest(request);
    setPendingMatchIndex(index);
    setPendingMatchTotal(total);
    return true;
  };

  // Songs the co-approval DJ holds are skipped, so the run moves on to the next one
  const openNextInRun = async (step: MatchRunStep, stillPendingIds: ReadonlySet<string>) => {
    let current = step;
    while (current.nextId) {
      const nextId = current.nextId;
      const next = pendingRequests.find(r => r.id === nextId);
      if (next) {
        setMatchRun(current.run);
        if (await openMatchModal(next, current.position, current.run.total)) return;
      }
      current = advanceMatchRun(current.run, nextId, stillPendingIds);
    }
    handleCloseMatchModal();
  };

  const handleApprove = async (requestId: string) => {
    const request = pendingRequests.find(r => r.id === requestId);
    if (!request) return;
    if (needsLinkMatch(request, playMode)) {
      openMatchModal(request, 0, 0);
    } else {
      await approveDirectly(requestId, request.spotifyUri);
    }
  };

  // --- Process Pending (batch with match modal) ---
  const handleProcessPending = () => {
    const available = pendingRequests.filter(r => !heldByHelper(r));
    if (available.length === 0) return;
    const run = startMatchRun(available.map(r => r.id));
    void openNextInRun({ run, nextId: run.remainingIds[0], position: 1 }, new Set(available.map(r => r.id)));
  };

  const advanceToNextPending = () => {
    if (!matchRun || !matchingRequest) {
      handleCloseMatchModal();
      return;
    }
    const stillPendingIds = new Set(pendingRequests.map(r => r.id));
    void openNextInRun(advanceMatchRun(matchRun, matchingRequest.id, stillPendingIds), stillPendingIds);
  };

  // Fresh-read co-approval setting from Firestore to avoid stale context
  const readCoApprovalEnabled = async (): Promise<boolean> => {
    if (!eventId) return coApprovalEnabled;
    try {
      const freshEvent = await getEventById(eventId);
      return freshEvent ? freshEvent.settings.coApprovalEnabled || false : coApprovalEnabled;
    } catch (err) {
      console.error('[PendingPage] Failed to read co-approval setting, using cached event settings:', err);
      return coApprovalEnabled;
    }
  };

  // --- SongMatchModal callbacks ---
  const handleModalApproveWithPlatformLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: SongSearchResult | null,
    durationOverrideMs: number | null,
    durationVerified: 'exact' | 'approximate' | null,
  ) => {
    if (matchingNomination) {
      try {
        const existing = nominations.find(r => r.id === requestId);
        const routeToCoApprover = shouldRouteToCoApprover(await readCoApprovalEnabled(), existing?.coApprovalStatus);
        const updateData: Record<string, unknown> = {
          ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { poolApproved: true }),
          ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
          preppedInSpotify: platformName === 'Spotify',
          timestamps: {
            requested: existing?.timestamps?.requested || new Date().toISOString(),
            ...existing?.timestamps,
            approved: new Date().toISOString(),
          },
        };
        if (itunesResult) {
          updateData.song = {
            ...existing?.song,
            title: itunesResult.trackName,
            artist: itunesResult.artistName,
            albumArtUrl: itunesResult.artworkUrl100 || existing?.song?.albumArtUrl || '',
            durationMs: itunesResult.trackTimeMillis || existing?.song?.durationMs || 0,
            explicitFlag: itunesResult.trackExplicitness === 'explicit' || existing?.song?.explicitFlag || false,
            itunesTrackId: String(itunesResult.trackId),
            isrc: isrcForMatchedSong(itunesResult, existing?.song ?? {}),
            previewUrl: itunesResult.previewUrl || existing?.song?.previewUrl || null,
          };
          for (const key of Object.keys(updateData.song as Record<string, unknown>)) {
            if ((updateData.song as Record<string, unknown>)[key] === undefined) {
              (updateData.song as Record<string, unknown>)[key] = null;
            }
          }
        }
        if (durationOverrideMs || durationVerified) {
          if (!updateData.song && existing) updateData.song = { ...existing.song };
          updateData.durationVerified = durationVerified || null;
          if (durationOverrideMs && updateData.song) {
            (updateData.song as Record<string, unknown>).durationMs = durationOverrideMs;
          }
        }
        Object.assign(updateData, linkFieldsFor(platformLink, platformName, songLinkUrl, updateData.song, existing));
        await updateRequest(requestId, updateData);
        setNominations(prev => prev.filter(r => r.id !== requestId));
        toast.success(
          routeToCoApprover ? 'Sent to co-approver for review' : `"${existing?.song.title}" matched and approved to People's Pick pool`,
          { icon: '👑' },
        );
      } catch {
        toast.error('Failed to approve nomination');
      }
      setMatchingNomination(false);
      handleCloseMatchModal();
      return;
    }
    await approveWithPlatformLink(requestId, platformLink, platformName, songLinkUrl, itunesResult);
    if (durationOverrideMs || durationVerified) {
      const update: Record<string, unknown> = { durationVerified: durationVerified || null };
      if (durationOverrideMs) {
        const existing = pendingRequests.find(r => r.id === requestId);
        if (existing) {
          update.song = { ...existing.song, durationMs: durationOverrideMs };
          for (const key of Object.keys(update.song as Record<string, unknown>)) {
            if ((update.song as Record<string, unknown>)[key] === undefined) {
              (update.song as Record<string, unknown>)[key] = null;
            }
          }
        }
      }
      await updateRequest(requestId, update).catch(() => {});
    }
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalReject = async (requestId: string, reason: string) => {
    if (matchingNomination) {
      try {
        const existing = nominations.find(r => r.id === requestId);
        await updateRequest(requestId, {
          status: 'rejected',
          rejectionReason: reason,
          timestamps: { requested: existing?.timestamps?.requested || new Date().toISOString(), rejected: new Date().toISOString() },
        });
        setNominations(prev => prev.filter(r => r.id !== requestId));
        toast.success('Nomination rejected');
      } catch {
        toast.error('Failed to reject nomination');
      }
      setMatchingNomination(false);
      handleCloseMatchModal();
      return;
    }
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      await updateRequest(requestId, {
        status: 'rejected',
        rejectionReason: reason,
        timestamps: { requested: existing?.timestamps?.requested || new Date().toISOString(), ...existing?.timestamps, rejected: new Date().toISOString() },
      });
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Request rejected');
    } catch {
      toast.error('Failed to reject');
    }
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalDefer = async (requestId: string) => {
    if (matchingNomination) {
      setMatchingNomination(false);
      handleCloseMatchModal();
      toast('Nomination deferred — come back later', { icon: '🔍' });
      return;
    }
    await updateRequest(requestId, { djNotes: 'Researching — needs platform match' });
    toast('Marked as "Researching" — come back to approve later', { icon: '🔍' });
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalApproveDirect = async (requestId: string) => {
    if (matchingNomination) {
      await approveNominationDirectly(requestId);
      setMatchingNomination(false);
      handleCloseMatchModal();
      return;
    }
    await approveDirectly(requestId);
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalSkip = () => {
    if (matchingNomination) {
      setMatchingNomination(false);
      handleCloseMatchModal();
      return;
    }
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleCloseMatchModal = () => {
    reviewHold.release();
    setMatchingRequest(null);
    setPendingMatchIndex(0);
    setPendingMatchTotal(0);
    setMatchRun(null);
    setMatchingNomination(false);
  };

  // --- Reject handlers ---
  const handleReject = (requestId: string) => {
    const request = pendingRequests.find(r => r.id === requestId);
    if (request) { setRejectingRequest(request); setRejectionReason(''); }
  };

  const confirmReject = async () => {
    if (!rejectingRequest || processingIds.has(rejectingRequest.id)) return;
    setProcessingIds(prev => new Set(prev).add(rejectingRequest.id));
    try {
      await updateRequest(rejectingRequest.id, {
        status: 'rejected',
        rejectionReason: rejectionReason.trim() || undefined,
        timestamps: { ...rejectingRequest.timestamps, rejected: new Date().toISOString() },
      });
      setPendingRequests(prev => prev.filter(r => r.id !== rejectingRequest.id));
      setRejectingRequest(null);
      setRejectionReason('');
      toast.success('Request rejected');
    } catch {
      toast.error('Failed to reject request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(rejectingRequest.id); return s; });
    }
  };

  const cancelReject = () => { setRejectingRequest(null); setRejectionReason(''); };

  // --- Bulk actions ---
  const handleBulkApprove = async () => {
    if (isBulkProcessing) return;
    setIsBulkProcessing(true);
    try {
      // Fresh-read co-approval setting from Firestore
      let isCoApprovalOn = coApprovalEnabled;
      if (eventId) {
        try {
          const freshEvent = await getEventById(eventId);
          if (freshEvent) isCoApprovalOn = freshEvent.settings.coApprovalEnabled || false;
        } catch { /* fall back to context value */ }
      }
      const approvable = pendingRequests.filter(r => !heldByHelper(r));
      const skipped = pendingRequests.length - approvable.length;
      await Promise.all(approvable.map(r => {
        const routeToCoApprover = shouldRouteToCoApprover(isCoApprovalOn, r.coApprovalStatus);
        const targetStatus = r.isOverflow ? 'overflow' : 'queued';
        const updateData: Record<string, unknown> = {
          ...(routeToCoApprover
            ? { coApprovalStatus: 'pending' }
            : { status: targetStatus }
          ),
          ...staleCoApprovalFields(routeToCoApprover, r.coApprovalStatus),
          timestamps: { ...r.timestamps, approved: new Date().toISOString() },
        };
        return updateRequest(r.id, updateData);
      }));
      const count = approvable.length;
      setPendingRequests(prev => prev.filter(r => !approvable.some(approved => approved.id === r.id)));
      setShowBulkApproveConfirm(false);
      const skippedNote = skipped > 0 ? ` — ${skipped} skipped (your co-approval DJ is reviewing)` : '';
      toast.success((isCoApprovalOn
        ? `${count} request${count !== 1 ? 's' : ''} sent to co-approver`
        : `${count} request${count !== 1 ? 's' : ''} approved`) + skippedNote
      );
    } catch { toast.error('Failed to approve requests'); }
    finally { setIsBulkProcessing(false); }
  };

  const handleBulkReject = async () => {
    if (isBulkProcessing) return;
    setIsBulkProcessing(true);
    try {
      await Promise.all(pendingRequests.map(r => updateRequest(r.id, {
        status: 'rejected', timestamps: { ...r.timestamps, rejected: new Date().toISOString() },
      })));
      const count = pendingRequests.length;
      setPendingRequests([]);
      setShowBulkRejectConfirm(false);
      toast.success(`${count} request${count !== 1 ? 's' : ''} rejected`);
    } catch { toast.error('Failed to reject requests'); }
    finally { setIsBulkProcessing(false); }
  };

  const approveNominationDirectly = async (requestId: string) => {
    if (!eventId) return;
    try {
      const existing = nominations.find(r => r.id === requestId);
      const routeToCoApprover = shouldRouteToCoApprover(await readCoApprovalEnabled(), existing?.coApprovalStatus);
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { poolApproved: true }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      await updateRequest(requestId, updateData);
      setNominations(prev => prev.filter(r => r.id !== requestId));
      toast.success(
        routeToCoApprover ? 'Sent to co-approver for review' : `"${existing?.song.title}" approved to People's Pick pool`,
        { icon: '👑' },
      );
    } catch {
      toast.error('Failed to approve nomination');
    }
  };

  const handleApproveNomination = async (requestId: string) => {
    const request = nominations.find(r => r.id === requestId);
    if (!request) return;
    setMatchingNomination(true);
    if (needsLinkMatch(request, playMode)) {
      if (!(await openMatchModal(request, 0, 0))) setMatchingNomination(false);
    } else {
      await approveNominationDirectly(requestId);
      setMatchingNomination(false);
    }
  };

  const handleRejectNomination = async (requestId: string, title: string) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const req = nominations.find(r => r.id === requestId);
      const now = new Date().toISOString();
      await updateRequest(requestId, {
        status: 'rejected',
        timestamps: { requested: req?.timestamps?.requested || now, ...req?.timestamps, rejected: now },
      });
      setNominations(prev => prev.filter(r => r.id !== requestId));
      toast.success(`"${title}" nomination rejected`);
    } catch {
      toast.error('Failed to reject nomination');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
  };

  const formatTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="animate-pulse">Loading pending requests...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Pending Requests</h2>
          <p className="text-gray-600 dark:text-gray-400">Review and approve song requests</p>
        </div>
        <div className="flex items-center gap-4">
          {!isLocked && pendingRequests.length > 0 && (
            <button
              onClick={handleProcessPending}
              className="btn btn-sm btn-primary flex items-center gap-1.5"
            >
              <Inbox className="w-4 h-4" />
              Process Pending ({pendingRequests.length})
            </button>
          )}
          {!isLocked && pendingRequests.length > 1 && (
            <div className="flex items-center gap-2">
              <button onClick={() => setShowBulkApproveConfirm(true)} className="btn btn-sm bg-success/10 text-success hover:bg-success/20 flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Approve All
              </button>
              <button onClick={() => setShowBulkRejectConfirm(true)} className="btn btn-sm bg-danger/10 text-danger hover:bg-danger/20 flex items-center gap-1">
                <XCircle className="w-4 h-4" /> Reject All
              </button>
            </div>
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">{pendingRequests.length} pending</span>
        </div>
      </div>

      {/* Bulk Approve Confirmation */}
      {showBulkApproveConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Approve All Requests?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to approve all {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowBulkApproveConfirm(false)} disabled={isBulkProcessing} className="btn btn-secondary">Cancel</button>
              <button onClick={handleBulkApprove} disabled={isBulkProcessing} className={`btn bg-success text-white hover:bg-success/90 ${isBulkProcessing ? 'opacity-50' : ''}`}>
                {isBulkProcessing ? 'Processing...' : 'Approve All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reject Confirmation */}
      {showBulkRejectConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Reject All Requests?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to reject all {pendingRequests.length} pending request{pendingRequests.length !== 1 ? 's' : ''}?
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowBulkRejectConfirm(false)} disabled={isBulkProcessing} className="btn btn-secondary">Cancel</button>
              <button onClick={handleBulkReject} disabled={isBulkProcessing} className={`btn bg-danger text-white hover:bg-danger/90 ${isBulkProcessing ? 'opacity-50' : ''}`}>
                {isBulkProcessing ? 'Processing...' : 'Reject All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual Reject Dialog */}
      {rejectingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Reject Request?</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Reject "{rejectingRequest.song.title}" by {rejectingRequest.song.artist}?
            </p>
            <div className="mb-4">
              <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason (optional)</label>
              <textarea id="rejection-reason" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Already played tonight, Not suitable for this venue..." className="input w-full h-20 resize-none" maxLength={200} />
              <p className="text-xs text-gray-500 mt-1">{rejectionReason.length}/200 characters</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={cancelReject} disabled={processingIds.has(rejectingRequest.id)} className="btn btn-secondary">Cancel</button>
              <button onClick={confirmReject} disabled={processingIds.has(rejectingRequest.id)}
                className={`btn bg-danger text-white hover:bg-danger/90 ${processingIds.has(rejectingRequest.id) ? 'opacity-50' : ''}`}>
                {processingIds.has(rejectingRequest.id) ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Request List */}
      {pendingRequests.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Pending Requests</h3>
          <p className="text-gray-600 dark:text-gray-400">New song requests from attendees will appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pendingRequests.map((request) => (
            <div key={request.id} className={`flex items-center gap-4 p-4 rounded-lg ${
              request.song.explicitFlag ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' : 'bg-gray-50 dark:bg-gray-800/50'
            }`}>
              {request.song.albumArtUrl ? (
                <img src={request.song.albumArtUrl} alt={`${request.song.title} album art`} className="w-12 h-12 rounded-lg flex-shrink-0 object-cover" />
              ) : (
                <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                  <Music className="w-6 h-6 text-gray-400" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                  <span className="truncate">{request.song.title}</span>
                  {request.song.explicitFlag && <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>}
                  {request.contested && <span className="flex-shrink-0" title="Contested duplicate"><Flag className="w-4 h-4 text-amber-500" /></span>}
                  {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'passed' && (
                    <span className="flex-shrink-0" title="Previously passed"><ShieldCheck className="w-3.5 h-3.5 text-green-500" /></span>
                  )}
                  {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'blocked' && (
                    <span className="flex-shrink-0" title="Previously blocked"><ShieldX className="w-3.5 h-3.5 text-red-500" /></span>
                  )}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate flex items-center gap-2">
                  <span className="truncate">{request.song.artist}</span>
                  <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 font-medium tabular-nums">
                    {formatElapsed(request.createdAt)}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Requested by {attendeeDisplayName(request.requestedBy.nickname, request.requestedBy.oderId)} &bull; {formatTime(request.timestamps.requested)}
                </p>
                {request.isOverflow && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400">
                    Overflow
                  </span>
                )}
                {request.prizeSkipTheLine && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    🏆 Prize pick
                  </span>
                )}
                {heldByHelper(request) && (
                  <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-medium">
                    {holderLabel(request.reviewHold)} is reviewing
                  </p>
                )}
                <ReviewedByBadge reviewedBy={request.reviewedBy} />
                {request.message && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-primary">
                    <MessageSquare className="w-3 h-3" />
                    <span className="italic">"{request.message}"</span>
                  </div>
                )}
                {request.song.explicitFlag && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Explicit content — use AI analysis to review
                  </p>
                )}
                {/* Co-Approval status badges */}
                {isWaitingOnCoApprover(coApprovalEnabled, request.coApprovalStatus) && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium flex items-center gap-1">
                    <ShieldAlert className="w-3 h-3" /> Awaiting Co-Approval
                  </p>
                )}
                {request.coApprovalStatus === 'rejected' && (
                  <div className="mt-1">
                    <p className="text-xs text-red-600 dark:text-red-400 font-medium flex items-center gap-1">
                      <ShieldX className="w-3 h-3" /> Co-Approver Rejected
                    </p>
                    {request.coApprovalReason && (
                      <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 italic">
                        "{request.coApprovalReason}"
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => handleAnalyzeSong(request.song.title, request.song.artist)}
                  className={`p-2 rounded-lg transition-colors ${request.song.explicitFlag ? 'text-purple-600 bg-purple-100 dark:bg-purple-900/30' : 'text-purple-500 hover:bg-purple-500/10'}`}
                  title="AI Song Analysis">
                  <Sparkles className="w-4 h-4" />
                </button>
                {/* Co-approval rejected: Override & Queue or Accept Rejection */}
                {request.coApprovalStatus === 'rejected' ? (
                  <>
                    <button
                      onClick={async () => {
                        setProcessingIds(prev => new Set(prev).add(request.id));
                        try {
                          await updateRequest(request.id, { status: 'queued', coApprovalStatus: 'approved' });
                          setPendingRequests(prev => prev.filter(r => r.id !== request.id));
                          toast.success('Override: moved to queue');
                        } catch { toast.error('Failed to override'); }
                        finally { setProcessingIds(prev => { const s = new Set(prev); s.delete(request.id); return s; }); }
                      }}
                      disabled={processingIds.has(request.id)}
                      className={`p-2 text-amber-500 hover:bg-amber-500/10 rounded-lg transition-colors ${processingIds.has(request.id) ? 'opacity-50' : ''}`}
                      title="Override & Queue"
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={async () => {
                        setProcessingIds(prev => new Set(prev).add(request.id));
                        try {
                          await updateRequest(request.id, { status: 'rejected', rejectionReason: request.coApprovalReason || 'Rejected by co-approver' });
                          setPendingRequests(prev => prev.filter(r => r.id !== request.id));
                          toast.success('Rejection accepted');
                        } catch { toast.error('Failed'); }
                        finally { setProcessingIds(prev => { const s = new Set(prev); s.delete(request.id); return s; }); }
                      }}
                      disabled={processingIds.has(request.id)}
                      className={`p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors ${processingIds.has(request.id) ? 'opacity-50' : ''}`}
                      title="Accept Rejection"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                ) : isWaitingOnCoApprover(coApprovalEnabled, request.coApprovalStatus) ? (
                  /* Already sent to co-approver — no approve/reject buttons */
                  null
                ) : isLocked ? null : (
                  <>
                    <button onClick={() => handleApprove(request.id)} disabled={processingIds.has(request.id) || heldByHelper(request)}
                      className={`p-2 text-success hover:bg-success/10 rounded-lg transition-colors ${actionDimmed(request)}`}
                      title={actionTitle(request, 'Approve')}>
                      <Check className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleReject(request.id)} disabled={processingIds.has(request.id) || heldByHelper(request)}
                      className={`p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors ${actionDimmed(request)}`}
                      title={actionTitle(request, 'Reject')}>
                      <X className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* People's Pick Nominations — hidden via feature flag */}
      {!PEOPLES_PICK_HIDDEN && eventData?.settings?.superRequestEnabled && (
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2">
            👑 People's Pick Nominations ({nominations.length})
          </h3>
          {nominations.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No unreviewed nominations</p>
          ) : (
            <div className="space-y-2">
              {nominations.map((req) => (
                <div key={req.id} className="flex items-center gap-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
                  {req.song.albumArtUrl ? (
                    <img src={req.song.albumArtUrl} alt="" className="w-10 h-10 rounded flex-shrink-0 object-cover" />
                  ) : (
                    <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded flex items-center justify-center flex-shrink-0">
                      <Music className="w-5 h-5 text-amber-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                      <span className="text-amber-500">👑</span>
                      <span className="truncate">{req.song.title}</span>
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{req.song.artist}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Nominated by {attendeeDisplayName(req.requestedBy.nickname, req.requestedBy.oderId)} &bull; {formatTime(req.timestamps.requested)}
                    </p>
                    {isWaitingOnCoApprover(coApprovalEnabled, req.coApprovalStatus) && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium flex items-center gap-1">
                        <ShieldAlert className="w-3 h-3" /> Awaiting Co-Approval
                      </p>
                    )}
                    {heldByHelper(req) && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-medium">
                        {holderLabel(req.reviewHold)} is reviewing
                      </p>
                    )}
                    <ReviewedByBadge reviewedBy={req.reviewedBy} />
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleApproveNomination(req.id)}
                      disabled={processingIds.has(req.id) || heldByHelper(req)}
                      className={`p-2 text-success hover:bg-success/10 rounded-lg transition-colors ${actionDimmed(req)}`}
                      title={actionTitle(req, 'Approve to Pool')}
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRejectNomination(req.id, req.song.title)}
                      disabled={processingIds.has(req.id) || heldByHelper(req)}
                      className={`p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors ${actionDimmed(req)}`}
                      title={actionTitle(req, 'Reject')}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Modal */}
      {analysisModal.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseAnalysisModal} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" /> AI Song Analysis
              </h3>
              <button onClick={handleCloseAnalysisModal} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="mb-3">
              <p className="font-medium text-gray-900 dark:text-white">{analysisModal.songTitle}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{analysisModal.songArtist}</p>
            </div>
            {analysisModal.loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto mb-3 text-purple-500 animate-spin" />
                <p className="text-gray-500 dark:text-gray-400">Analyzing song...</p>
              </div>
            ) : analysisModal.error ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-danger" />
                <p className="text-danger">{analysisModal.error}</p>
              </div>
            ) : analysisModal.analysis ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                  <p className="text-gray-900 dark:text-white">{analysisModal.analysis.summary}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Profanity Analysis</h4>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysisModal.analysis.profanityLevel === 'none' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : analysisModal.analysis.profanityLevel === 'mild' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                      : analysisModal.analysis.profanityLevel === 'moderate' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {analysisModal.analysis.profanityLevel === 'none' ? 'Clean' :
                       analysisModal.analysis.profanityLevel.charAt(0).toUpperCase() + analysisModal.analysis.profanityLevel.slice(1) + ' Profanity'}
                    </span>
                  </div>
                  <p className="text-gray-900 dark:text-white text-sm">{analysisModal.analysis.profanityAnalysis}</p>
                </div>
                <p className={`text-xs ${
                  analysisModal.analysis.lyricsSource === 'web_search' || analysisModal.analysis.lyricsSource === 'manual'
                    ? 'text-emerald-500 dark:text-emerald-400'
                    : 'text-amber-500 dark:text-amber-400'
                }`}>
                  {analysisModal.analysis.lyricsSource === 'web_search'
                    ? 'Analyzed with song lyrics \u00b7 Results are cached'
                    : analysisModal.analysis.lyricsSource === 'manual'
                    ? 'Analyzed with pasted lyrics \u00b7 Results are cached'
                    : 'Analyzed from AI knowledge (no lyrics found) \u00b7 Results are cached'}
                </p>

                {analysisModal.analysis.lyricsSource !== 'manual' && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Paste lyrics from Musixmatch for a more accurate analysis:</p>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${analysisModal.songTitle} ${analysisModal.songArtist}`);
                          toast.success('Copied to clipboard');
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        Copy Title & Artist
                      </button>
                      <button
                        onClick={() => window.open('https://www.musixmatch.com/search', '_blank')}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open Musixmatch
                      </button>
                    </div>
                    <textarea
                      value={lyricsPaste.text}
                      onChange={(e) => setLyricsPaste(prev => ({ ...prev, text: e.target.value }))}
                      placeholder="Paste lyrics here..."
                      rows={4}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={handleReanalyzeWithLyrics}
                      disabled={!lyricsPaste.text.trim() || lyricsPaste.loading}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {lyricsPaste.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {lyricsPaste.loading ? 'Re-analyzing...' : 'Re-analyze with Lyrics'}
                    </button>
                  </div>
                )}
              </div>
            ) : null}
            {/* Pass/Block verdict buttons */}
            {analysisModal.analysis && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                {(() => {
                  const key = `${analysisModal.songTitle.toLowerCase().trim()}|${analysisModal.songArtist.toLowerCase().trim()}`;
                  const currentVerdict = songVerdicts.get(key);
                  return (
                    <>
                      <button
                        onClick={() => handleSetVerdict(analysisModal.songTitle, analysisModal.songArtist, 'passed')}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentVerdict === 'passed'
                            ? 'bg-green-500 text-white'
                            : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/40'
                        }`}
                      >
                        <ShieldCheck className="w-4 h-4" />
                        {currentVerdict === 'passed' ? 'Passed' : 'Pass'}
                      </button>
                      <button
                        onClick={() => handleSetVerdict(analysisModal.songTitle, analysisModal.songArtist, 'blocked')}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          currentVerdict === 'blocked'
                            ? 'bg-red-500 text-white'
                            : 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/40'
                        }`}
                      >
                        <ShieldX className="w-4 h-4" />
                        {currentVerdict === 'blocked' ? 'Blocked' : 'Block'}
                      </button>
                      <div className="flex-1" />
                    </>
                  );
                })()}
                <button onClick={handleCloseAnalysisModal} className="btn btn-secondary">Close</button>
              </div>
            )}
            {!analysisModal.analysis && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button onClick={handleCloseAnalysisModal} className="w-full btn btn-secondary">Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Platform Match Modal */}
      <div {...reviewHold.activityHandlers}>
      <SongMatchModal
        request={matchingRequest}
        eventName={eventData?.name}
        batchIndex={pendingMatchIndex}
        batchTotal={pendingMatchTotal}
        playMode={playMode.id}
        djUid={user?.uid || ''}
        onApproveWithPlatformLink={handleModalApproveWithPlatformLink}
        onApproveDirect={handleModalApproveDirect}
        onReject={handleModalReject}
        onDefer={handleModalDefer}
        onSkip={handleModalSkip}
        onClose={handleCloseMatchModal}
        onAnalyzeSong={handleAnalyzeSong}
        holdBanner={<ReviewHoldBanner warning={reviewHold.warning} onKeep={reviewHold.renew} />}
      />
      </div>
    </div>
  );
}
